import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Fade,
  Box,
  Typography,
  IconButton,
  Backdrop,
} from '@mui/material';
import { Close, PlayArrow, CheckCircle } from '@mui/icons-material';

const DEFAULT_TOKENS = {
  colors: {
    primary: '#0D1C4F',
    secondary: '#1E3A8A',
    accent: '#016f57',
    text: '#1F2937',
    textLight: '#6B7280',
    light: '#F8FAFC',
    lightBlue: '#EFF6FF',
    success: '#016f57',
    gradient: 'linear-gradient(135deg, #0D1C4F 0%, #1E3A8A 100%)',
  },
  borderRadius: {
    lg: '16px',
    xl: '20px',
  },
  shadows: {
    xl: '0 16px 48px rgba(13, 28, 79, 0.25)',
  },
};

const DEFAULT_FEATURES = [
  'Find lost items in minutes using AI',
  'Connect with the right study partners',
  'Get instant academic help with AI',
  'Manage campus events effortlessly',
];

const PeerCampusDemoModal = ({
  open,
  onClose,
  designTokens: customTokens = DEFAULT_TOKENS,
  features = DEFAULT_FEATURES,
  title = '🎬 How PeerCampus Transforms Campus Life',
  subtitle = 'Explore how students, clubs, and admins use PeerCampus in under 3 minutes',
  onPlay,
  videoUrl,
}) => {
  const tokens = useMemo(() => ({
    colors: { ...DEFAULT_TOKENS.colors, ...(customTokens?.colors || {}) },
    borderRadius: { ...DEFAULT_TOKENS.borderRadius, ...(customTokens?.borderRadius || {}) },
    shadows: { ...DEFAULT_TOKENS.shadows, ...(customTokens?.shadows || {}) },
  }), [customTokens]);

  const featureList = Array.isArray(features) && features.length > 0 ? features : DEFAULT_FEATURES;

  const resolvedVideoUrl = videoUrl || '';
  const showVideo = Boolean(resolvedVideoUrl);

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: { backgroundColor: 'rgba(13, 28, 79, 0.8)' },
      }}
      aria-labelledby="video-demo-title"
      aria-describedby="video-demo-description"
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
            maxWidth: 900,
            maxHeight: '92vh',
            bgcolor: 'background.paper',
            borderRadius: tokens.borderRadius.xl,
            boxShadow: tokens.shadows.xl,
            p: { xs: 3, md: 5 },
            outline: 'none',
            border: `1px solid ${tokens.colors.primary}20`,
            overflowY: 'visible',
            boxSizing: 'border-box',
            '@media (max-height: 700px)': {
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              p: 2,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              '@media (max-height: 700px)': {
                mb: 2,
              },
            }}
          >
            <Box>
              <Typography
                id="video-demo-title"
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: tokens.colors.text,
                  fontSize: { xs: '1.5rem', md: '1.8rem' },
                }}
              >
                {title}
              </Typography>

              <Typography
                id="video-demo-description"
                variant="body2"
                sx={{
                  color: tokens.colors.textLight,
                  fontSize: '0.9rem',
                  mt: 0.75,
                }}
              >
                {subtitle}
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              aria-label="Close video demo"
              sx={{
                width: 34,
                height: 34,
                borderRadius: '9px',
                mt: 0.5,
                color: tokens.colors.primary,
                backgroundColor: `${tokens.colors.primary}08`,
                border: `1px solid ${tokens.colors.primary}20`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: `${tokens.colors.primary}14`,
                  borderColor: `${tokens.colors.primary}30`,
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  backgroundColor: `${tokens.colors.primary}18`,
                },
                '&.Mui-focusVisible': {
                  outline: `2px solid ${tokens.colors.accent}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>

          <Box
            sx={{
              width: '100%',
              height: {
                xs: 'clamp(200px, 36vh, 290px)',
                sm: 'clamp(248px, 46vh, 410px)',
                md: 'clamp(292px, 54vh, 520px)',
              },
              '@media (max-height: 700px)': {
                height: 'clamp(178px, 31vh, 250px)',
              },
              backgroundColor: tokens.colors.light,
              borderRadius: tokens.borderRadius.lg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              border: `2px dashed ${tokens.colors.primary}20`,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {showVideo ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                }}
              >
                <Box
                  component="iframe"
                  title="PeerCampus demo video"
                  src={resolvedVideoUrl}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  sx={{
                    width: '100%',
                    height: '100%',
                    border: 0,
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${tokens.colors.primary}05, ${tokens.colors.accent}05)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: tokens.colors.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: `0 8px 25px ${tokens.colors.primary}40`,
                    },
                  }}
                  role={onPlay ? 'button' : undefined}
                  tabIndex={onPlay ? 0 : -1}
                  onClick={onPlay}
                  onKeyDown={(event) => {
                    if (!onPlay) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onPlay();
                    }
                  }}
                >
                  <PlayArrow sx={{ fontSize: 40, color: 'white', ml: 1 }} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    color: tokens.colors.text,
                    fontWeight: 600,
                    textAlign: 'center',
                  }}
                >
                  Interactive Platform Demo
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: tokens.colors.textLight,
                    textAlign: 'center',
                    maxWidth: 300,
                  }}
                >
                  Experience the full PeerCampus journey from student onboarding to AI-powered features
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              mt: 2.75,
              p: 2.25,
              backgroundColor: `${tokens.colors.lightBlue}80`,
              borderRadius: tokens.borderRadius.lg,
              border: `1px solid ${tokens.colors.primary}08`,
              '@media (max-height: 700px)': {
                mt: 1.75,
                p: 1.85,
              },
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: tokens.colors.text,
                mb: 1.5,
                fontSize: '0.98rem',
              }}
            >
              What you'll see in this demo:
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                columnGap: 2,
                rowGap: 1.5,
                alignItems: 'start',
              }}
            >
              {featureList.map((feature, index) => (
                <Box
                  key={feature || index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.2,
                  }}
                >
                  <CheckCircle
                    sx={{
                      fontSize: 18,
                      color: tokens.colors.success,
                      mt: '2px',
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: tokens.colors.textLight,
                      fontSize: '0.85rem',
                      lineHeight: 1.45,
                      wordBreak: 'break-word',
                    }}
                  >
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

PeerCampusDemoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  designTokens: PropTypes.shape({
    colors: PropTypes.object,
    borderRadius: PropTypes.object,
    shadows: PropTypes.object,
  }),
  features: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onPlay: PropTypes.func,
  videoUrl: PropTypes.string,
};

export default PeerCampusDemoModal;
